"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SVGRenderer = exports.CROSSHAIR_CLASSES = void 0;
var tslib_1 = require("tslib");
var core_api_1 = require("@ikegps/core-api");
var lodash_1 = require("lodash");
var model_1 = require("../../../model");
var unit_1 = require("../../../unit");
var angle_1 = require("../../../unit/angle");
var xml_1 = require("../../../xml");
var label_draw_svg_1 = require("../../drawable/label.draw.svg");
var line_draw_svg_1 = require("../../drawable/line.draw.svg");
var marker_draw_svg_1 = require("../../drawable/marker.draw.svg");
var mask_draw_svg_1 = require("../../drawable/mask.draw.svg");
var util_draw_svg_1 = require("../../drawable/util.draw.svg");
var vertical_separation_draw_svg_1 = require("../../drawable/vertical.separation.draw.svg");
var mithril_1 = require("../../mithril");
var theme_1 = require("../../theme");
var crosshair_1 = require("../crosshair");
var rollIndicator_1 = require("../rollIndicator");
var rose_1 = require("../rose");
var svg_style_1 = require("./svg.style");
var timezones_1 = require("./timezones");
exports.CROSSHAIR_CLASSES = {
    innerLine: 'c-Draw-crosshair--innerline',
    outerLine: 'c-Draw-crosshair--outerline',
    distance: 'c-Draw-crosshair--distance'
};
var EXPORT_STRING_OPTIONS = {
    keyMap: { className: 'class' },
    ignoreKeys: ['oncreate', 'onupdate', 'onremove']
};
var DRAWABLE_PRIORITY = {
    hovered: 0,
    selected: 1,
    drawable: 2,
    mask: 3
};
var SVGRenderer = /** @class */ (function () {
    function SVGRenderer() {
    }
    SVGRenderer.toSVGString = function (draw, units, timezone, thumbnail) {
        if (thumbnail === void 0) { thumbnail = false; }
        var ctx = {
            draw: draw,
            units: units,
            timezone: timezone,
            isThumbnail: thumbnail,
            isInteractive: false,
            isEditable: false,
            isImageDisabled: true,
            scale: SVGRenderer.scaleUI(draw.state, thumbnail),
            isVerticalSeparationEnabled: true
        };
        var vNodes = SVGRenderer.view(ctx);
        return xml_1.IkeXML.Export.asString(vNodes, EXPORT_STRING_OPTIONS);
    };
    SVGRenderer.scaleUI = function (drawState, isThumbnail) {
        if (isThumbnail) {
            return drawState.scale;
        }
        return drawState.scale / drawState.zoom;
    };
    SVGRenderer.viewCrosshair = function (ctx) {
        if (!ctx.draw.state.isCrosshairVisible || ctx.draw.capture.distance == null) {
            return null;
        }
        var resolution = ctx.draw.capture.resolution;
        var center = { x: resolution.width / 2, y: resolution.height / 2 };
        var gap = 15;
        var size = { width: 150, height: 150 };
        return crosshair_1.Crosshair.view(ctx.draw, {
            center: center,
            size: size,
            gap: gap,
            units: ctx.units,
            classNames: exports.CROSSHAIR_CLASSES,
            scale: ctx.scale,
            outerStroke: 'rgba(0,0,0,0.5)',
            innerStroke: 'red',
            distance: ctx.draw.geometry3d.getDistance()
        });
    };
    SVGRenderer.viewRoll = function (ctx) {
        var _a, _b, _c;
        var roll = (_c = (_b = (_a = ctx.draw.capture.raw) === null || _a === void 0 ? void 0 : _a.orientation) === null || _b === void 0 ? void 0 : _b.roll) !== null && _c !== void 0 ? _c : null;
        if (!ctx.enableRoll || !ctx.draw.state.isRollVisible || roll === null) {
            return null;
        }
        return rollIndicator_1.RollIndicator.view(ctx.draw, {
            roll: roll,
            width: ctx.draw.capture.resolution.width,
            height: ctx.draw.capture.resolution.height,
            zoomLevel: ctx.draw.state.zoom,
            scale: ctx.scale,
            resHeight: ctx.draw.capture.resolution.height
        });
    };
    SVGRenderer.view = function (ctx) {
        var _a;
        ctx.scale = ctx.scale || this.scaleUI(ctx.draw.state, ctx.isThumbnail);
        var drawState = ctx.draw.drawState;
        var toDraw = drawState.values();
        var data = [];
        var capture = ctx.draw.capture;
        var zoomLevel = ctx.draw.state.zoom;
        var centerPoint = ctx.draw.state.center;
        var viewBox = getViewBox(capture.resolution, centerPoint, zoomLevel);
        data.push(SVGRenderer.viewCrosshair(ctx));
        data.push(SVGRenderer.viewRoll(ctx));
        var nodes = toDraw
            // Identify what type of drawable we're creating
            .map(function (drawable) {
            var id = drawable.id;
            var kind = model_1.CollectionModelDrawableMask.isMask(drawable) ? 'mask' : 'drawable';
            if (ctx.isInteractive) {
                if (id === ctx.draw.state.selectedId) {
                    kind = 'selected';
                }
                else if (id === ctx.draw.state.hoverId) {
                    kind = 'hovered';
                }
            }
            return {
                kind: kind,
                drawable: drawable
            };
        })
            // Sort the drawables so that the the hovered and selected drawables are rendered on top
            .sort(function (a, b) { return DRAWABLE_PRIORITY[b.kind] - DRAWABLE_PRIORITY[a.kind]; })
            // Get the mithril nodes
            .map(function (_a) {
            var drawable = _a.drawable, kind = _a.kind;
            return SVGRenderer.renderDrawable(ctx, drawable);
        })
            .filter(Boolean);
        var compassDrawable = drawState.baseOffset || drawState.base || null;
        nodes = tslib_1.__spreadArray(tslib_1.__spreadArray([], tslib_1.__read(nodes), false), [
            this.renderOrientationIndicator(ctx, compassDrawable, ctx.showOrientation || false),
            this.renderDateLabel(ctx, viewBox[3], zoomLevel),
            this.renderBearingLabel(ctx, viewBox[2], viewBox[3], zoomLevel)
        ], false);
        // if it's measurable (trusize image) we should not put this layer on the top of the markers
        // see bug: https://surveylab.atlassian.net/browse/IKE4-3258
        if (!ctx.isThumbnail && !ctx.draw.isIkePhoto) {
            var _b = tslib_1.__read([ctx.draw.state.cropX, ctx.draw.state.cropY, ctx.draw.state.cropWidth, ctx.draw.state.cropHeight], 4), x = _b[0], y = _b[1], width = _b[2], height = _b[3];
            //if there is no cropping region set, then all the capture is the cropping region
            if (ctx.draw.state.cropWidth === 0 || ctx.draw.state.cropHeight === 0) {
                _a = tslib_1.__read([0, 0, capture.resolution.width, capture.resolution.height], 4), x = _a[0], y = _a[1], width = _a[2], height = _a[3];
            }
            var cropView = (0, mithril_1.m)('rect', {
                className: theme_1.ClassNames.cropWindow,
                'fill-opacity': 0.0,
                width: width,
                height: height,
                x: x,
                y: y
            });
            if (zoomLevel > 1) {
                cropView.attrs['c-Draw-draggable'] = 'true';
                cropView.attrs.className += ' ' + theme_1.ClassNames.isMoveable;
                cropView.attrs['c-Draw-draggable'] = 'true';
                cropView.attrs['c-Draw-node-id'] = 'image';
                cropView.attrs['c-Draw-Label'] = util_draw_svg_1.IkeDrawLabel.IMAGE;
                cropView.attrs['c-Draw-tag'] = 'image';
            }
            else {
                cropView.attrs['c-Draw-draggable'] = 'false';
                cropView.attrs.className = theme_1.ClassNames.cropWindow;
                cropView.attrs['c-Draw-draggable'] = 'false';
                cropView.attrs['c-Draw-node-id'] = 'rect';
                //cropView.attrs['c-Draw-Label'] = IkeDrawLabel.IMAGE;
                //cropView.attrs['c-Draw-tag'] = 'rect';
            }
            nodes.push(cropView);
        }
        ctx.draw.log.debug({
            collection: ctx.draw.collection.id,
            capture: ctx.draw.capture.objectId,
            nodes: tslib_1.__spreadArray([], tslib_1.__read(nodes), false)
        }, 'SVGRenderer: rendering drawable nodes for capture');
        var drawableContainer = (0, mithril_1.m)('g', { className: 'c-Drawables' }, nodes);
        data.push(drawableContainer);
        data.push(SVGRenderer.renderImage(ctx, capture, zoomLevel));
        data.push(SVGRenderer.renderFilters(ctx));
        var rootSVG = (0, mithril_1.m)('svg', {
            className: theme_1.ClassNames.svg,
            style: svg_style_1.SVGStyles[theme_1.ClassNames.svg],
            'data-capture-id': drawState.captureId
        }, data.reverse());
        if (ctx.isInteractive) {
            ctx.draw.boundEvents.lastRenderOptions = ctx;
            rootSVG.attrs.onclick = ctx.draw.boundEvents.click;
            rootSVG.attrs.ondblclick = ctx.draw.boundEvents.doubleclick;
            rootSVG.attrs.onmousedown = ctx.draw.boundEvents.mousedown;
            rootSVG.attrs.onmousemove = ctx.draw.boundEvents.mousemove;
            rootSVG.attrs.onwheel = ctx.draw.boundWheelEvent;
            // No requirement for touch support.
            //rootSVG.attrs.ontouchmove = SVGEventHandler.mousemove.bind(null, this.dc);
            //rootSVG.attrs.ontouchstart = SVGEventHandler.mousedown.bind(null, this.dc);
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isInteractive;
        }
        else {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isStatic;
        }
        if (ctx.draw.isMoving) {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isMoving;
        }
        if (ctx.draw.isLinking) {
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isLinking;
        }
        var currentTool = ctx.draw.state.tool;
        if (ctx.isInteractive && currentTool != null) {
            rootSVG.attrs.className += " ".concat(theme_1.ClassNames.isToolActive, " c-Draw-Tool-").concat(currentTool);
        }
        if (ctx.isThumbnail) {
            rootSVG.attrs.width = ctx.size.width;
            rootSVG.attrs.height = ctx.size.height;
            rootSVG.attrs.className += ' ' + theme_1.ClassNames.isThumbnail;
            rootSVG.attrs.preserveAspectRatio = 'xMinYMin slice';
            rootSVG.attrs.viewBox = getThumbnailViewBox(capture.resolution, ctx.size).join(' ');
            if (zoomLevel > 1) {
                var _c = tslib_1.__read(viewBox, 4), x = _c[0], y = _c[1], width = _c[2], height = _c[3];
                var currentView = (0, mithril_1.m)('rect', {
                    className: theme_1.ClassNames.thumbnailZoomWindow,
                    fill: 'rgba(0,0,0,0.35)',
                    width: width,
                    height: height,
                    x: x,
                    y: y
                });
                rootSVG.children.push(currentView);
            }
        }
        else {
            rootSVG.attrs.viewBox = viewBox.join(' ');
            rootSVG.attrs.preserveAspectRatio = 'xMinYMin';
        }
        var rootClassName = theme_1.ClassNames.container;
        if (ctx.isThumbnail) {
            rootClassName += ' ' + theme_1.ClassNames.isThumbnail;
        }
        return (0, mithril_1.m)('div', {
            className: rootClassName, style: ctx.draw.orientation === 'landscape' ? {
                display: 'flex',
                'flex-direction': 'column',
                'justify-content': 'center',
                'align-items': 'center'
            } : {}
        }, rootSVG);
    };
    SVGRenderer.filterId = function (dc) {
        return "".concat(dc.id, ":brightness");
    };
    SVGRenderer.renderFilters = function (ctx) {
        // https://github.com/iamvdo/pleeease-filters
        var feFuncAttrs = { type: 'gamma', exponent: ctx.draw.state.brightness };
        return (0, mithril_1.m)('defs', null, [
            (0, mithril_1.m)('filter', { id: SVGRenderer.filterId(ctx.draw) }, [
                (0, mithril_1.m)('feComponentTransfer', { 'color-interpolation-filters': 'sRGB' }, [
                    (0, mithril_1.m)('feFuncR', feFuncAttrs),
                    (0, mithril_1.m)('feFuncG', feFuncAttrs),
                    (0, mithril_1.m)('feFuncB', feFuncAttrs)
                ])
            ])
        ]);
    };
    SVGRenderer.renderDateLabel = function (ctx, height, zoomLevel) {
        if (!ctx.draw.state.isDateLabelVisible) {
            return null;
        }
        var rectHeight = ctx.scale * label_draw_svg_1.LabelSVGDraw.height(1);
        var yPos = (height * zoomLevel) - rectHeight;
        var xOffset = label_draw_svg_1.LabelSVGDraw.Size.width * ctx.scale / 2;
        var node = (0, mithril_1.m)('g', {
            transform: "translate(0, ".concat(yPos, ")")
        }, [
            (0, mithril_1.m)('rect', {
                x: '0',
                y: '0',
                width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                height: rectHeight,
                fill: 'rgb(224,224,224)',
                'fill-opacity': 0.75,
                stoke: 'none',
                'stroke-width': 0
            }),
            (0, mithril_1.m)('g', { transform: "translate(".concat(xOffset, ", 0) scale(").concat(ctx.scale, ")") }, [
                (0, mithril_1.m)('text', {
                    style: 'font-size: 14px; user-select: none; pointer-events: none;',
                    x: '0',
                    y: '1em',
                    width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                    'text-anchor': 'middle',
                    dy: '0.25em'
                }, formatDateTime(ctx.draw.capture.collectedAt.iso, ctx.timezone))
            ])
        ]);
        return node;
    };
    SVGRenderer.renderBearingLabel = function (ctx, width, height, zoomLevel) {
        if (!ctx.draw.state.isBearingLabelVisible || ctx.draw.capture.orientation == null) {
            return null;
        }
        var rectHeight = ctx.scale * label_draw_svg_1.LabelSVGDraw.height(1);
        var rectWidth = ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width;
        var xOffset = rectWidth / 2;
        var yPos = (height * zoomLevel) - rectHeight;
        var xPos = ((width * zoomLevel) - rectWidth) / 2;
        var node = (0, mithril_1.m)('g', {
            transform: "translate(".concat(xPos, ", ").concat(yPos, ")")
        }, [
            (0, mithril_1.m)('rect', {
                x: '0',
                y: '0',
                width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                height: rectHeight,
                fill: 'rgb(224,224,224)',
                'fill-opacity': 0.75,
                stoke: 'none',
                'stroke-width': 0
            }),
            (0, mithril_1.m)('g', { transform: "translate(".concat(xOffset, ", 0)scale(").concat(ctx.scale, ")") }, [
                (0, mithril_1.m)('text', {
                    style: 'font-size: 14px; user-select: none; pointer-events: none;',
                    x: '0',
                    y: '1em',
                    width: ctx.scale * label_draw_svg_1.LabelSVGDraw.Size.width,
                    'text-anchor': 'middle',
                    dy: '0.25em'
                }, unit_1.IkeUnit.displayAngle(ctx.draw.capture.orientation.bearing, core_api_1.IkeUnitAngle.DEGREE, core_api_1.IkeUnitAngle.RADIAN, 0))
            ])
        ]);
        return node;
    };
    SVGRenderer.renderImage = function (ctx, image, zoomFactor) {
        if (ctx.isImageDisabled) {
            return null;
        }
        if (!ctx.isThumbnail && !ctx.draw.isImageReady(image.objectId)) {
            ctx.draw.loadImage(image.objectId, image.file.url);
            return null;
        }
        var href = image.file.url;
        var width = image.resolution.width;
        var height = image.resolution.height;
        if (ctx.isThumbnail && image.thumbnail) {
            href = image.thumbnail.url;
        }
        var imageNode = (0, mithril_1.m)('image', {
            className: theme_1.ClassNames.image,
            width: width,
            height: height,
            x: 0,
            y: 0,
            'xlink:href': href,
            // Stop firefox from grabbing the image
            draggable: false,
            ondragstart: function (e) { return e.preventDefault(); }
        });
        if (zoomFactor > 1) {
            imageNode.attrs.className += ' ' + theme_1.ClassNames.isMoveable;
            imageNode.attrs['c-Draw-draggable'] = 'true';
            imageNode.attrs['c-Draw-node-id'] = 'image';
            imageNode.attrs['c-Draw-Label'] = util_draw_svg_1.IkeDrawLabel.IMAGE;
            imageNode.attrs['c-Draw-tag'] = 'image';
        }
        if (ctx.draw.state.brightness !== 1) {
            imageNode.attrs['filter'] = "url(#".concat(SVGRenderer.filterId(ctx.draw), ")");
        }
        imageNode.attrs.className += ' ' + theme_1.ClassNames.isImageLoaded;
        return imageNode;
    };
    SVGRenderer.renderOrientationIndicator = function (ctx, obj, withOrientationIndicator) {
        if (!withOrientationIndicator || !obj) {
            return null;
        }
        var opts = {
            y: obj.y,
            captureBearing: (0, lodash_1.get)(obj, ['drawableState', 'capture', 'orientation', 'bearing'], null),
            orientationBearing: ctx.orientationBearing,
            color: ctx.indicatorColor
        };
        if (typeof opts.orientationBearing === 'number') {
            var northRotation = 360 - angle_1.IkeAngle.toDegrees(opts.captureBearing);
            var indicatorRotation = northRotation + angle_1.IkeAngle.toDegrees(opts.orientationBearing);
            return (0, mithril_1.m)('svg', {
                class: 'c-Draw-orientation-indicator',
                y: "".concat(opts.y, "px"),
                x: '50%',
                style: {
                    overflow: 'visible',
                    'z-index': -10,
                    color: opts.color
                }
            }, (0, mithril_1.m)('g', {
                style: {
                    transform: 'translate3d(-200px, -65px, 1px)'
                }
            }, (0, mithril_1.m)('g', {
                style: {
                    transform: "perspective(300px) rotateX(70deg)"
                }
            }, (0, mithril_1.m)('g', {
                transform: "rotate(".concat(indicatorRotation, ", 200, 200)")
            }, rose_1.rose))));
        }
        return null;
    };
    SVGRenderer.renderDrawable = function (ctx, obj) {
        var _this = this;
        var node;
        if (model_1.CollectionModelDrawableMarker.isMarker(obj)) {
            node = marker_draw_svg_1.MarkerSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableMarkerOffset.isBaseOffset(obj)) {
            node = marker_draw_svg_1.MarkerSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableLabel.isLabel(obj) && obj.parent == null) {
            node = label_draw_svg_1.LabelSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableLine.isLine(obj)) {
            node = line_draw_svg_1.LineSVGDraw.view(obj, ctx);
        }
        else if (model_1.CollectionModelDrawableMask.isMask(obj)) {
            var maskFetcher_1 = ctx.draw.captureMaskFetcher;
            if (maskFetcher_1) {
                obj.fetchSvgMask(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () { return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, maskFetcher_1(ctx.draw.collection.id, ctx.draw.capture.objectId)];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                }); }); }).catch(function (error) {
                    // We have to catch and log the error here or the app might crash
                    // due to an uncaught promise rejection.
                    ctx.draw.log.error({ error: error }, 'SVGRenderer: caught error while fetching mask');
                });
            }
            node = mask_draw_svg_1.MaskSVGDraw.view(obj, ctx);
        }
        else if (obj instanceof model_1.CollectionModelDrawableVerticalSeparation) {
            node = vertical_separation_draw_svg_1.VerticalSeparationSVGDraw.view(obj, ctx);
        }
        if (node) {
            node.key = obj.id;
        }
        return node;
    };
    SVGRenderer.Style = svg_style_1.SVGStyles;
    SVGRenderer.CSS = theme_1.ClassNames;
    return SVGRenderer;
}());
exports.SVGRenderer = SVGRenderer;
function getThumbnailViewBox(resolution, thumbnailSize) {
    var dimensions = scaleCenterCrop(resolution, thumbnailSize);
    var viewX = dimensions.left * 1 / dimensions.scale;
    var viewY = dimensions.top * 1 / dimensions.scale;
    return [-viewX, -viewY, resolution.width + viewX, resolution.height + viewY];
}
function getViewBox(resolution, center, zoomFactor) {
    var zoomWidth = resolution.width / zoomFactor / 2;
    var zoomHeight = resolution.height / zoomFactor / 2;
    if (center.x - zoomWidth < 0) {
        center.x = zoomWidth;
    }
    else if (center.x + zoomWidth > resolution.width) {
        center.x = resolution.width - zoomWidth;
    }
    if (center.y - zoomHeight < 0) {
        center.y = zoomHeight;
    }
    else if (center.y + zoomHeight > resolution.height) {
        center.y = resolution.height - zoomHeight;
    }
    return [center.x - zoomWidth, center.y - zoomHeight, zoomWidth * 2, zoomHeight * 2];
}
function scaleCenterCrop(source, dest) {
    // Compute the scaling factors to fit the new height and width, respectively.
    // To cover the final image, the final scaling will be the bigger
    // of these two.
    var xScale = dest.width / source.width;
    var yScale = dest.height / source.height;
    var scale = Math.max(xScale, yScale);
    // Now get the size of the source.bitmap when scaled
    var scaledWidth = scale * source.width;
    var scaledHeight = scale * source.height;
    // Let's find out the upper left coordinates if the scaled bitmap
    // should be centered in the new size give by the parameters
    var left = (dest.width - scaledWidth) / 2;
    var top = (dest.height - scaledHeight) / 2;
    return {
        top: top,
        left: left,
        width: scaledWidth > dest.width ? dest.width : scaledWidth,
        height: scaledHeight > dest.height ? dest.height : scaledHeight,
        scale: scale
    };
}
function formatDateTime(isoDate, timezone) {
    var locale = 'en-GB';
    var searchString = isoDate.slice(0, -5) + '.000Z';
    var date = new Date(searchString);
    var timeZone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    var datePart = "".concat(date.toLocaleString(locale, { month: 'short', day: '2-digit', timeZone: timeZone }), ", ").concat(date.toLocaleString(locale, { year: 'numeric', timeZone: timeZone }), " ");
    var output = datePart + ' ' + date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: timeZone });
    if (timeZone === 'UTC') {
        return output + ' (UTC)';
    }
    var tzshort = getTimezoneShort(timeZone);
    return "".concat(output, " (").concat(tzshort, ")");
}
function getTimezoneShort(tz) {
    var result = timezones_1.Timezones.getTimezones().find(function (_a) {
        var utc = _a.utc;
        return utc.indexOf(tz) > 0;
    });
    if (result === undefined) {
        return 'N/A';
    }
    return result.abbr;
}
//# sourceMappingURL=svg.render.js.map